/**
 * FlowCompletion + FlowErrorBanner
 *
 * Shared "what happens after the user finishes the guide" surface
 * adopted by every CoPilot host (CheckUp, Care, QultureUp, TelePortal,
 * Journey-Builder).
 *
 * Replaces the five bespoke completion banners that diverged in copy,
 * styling, redirect-countdown UX, and error-handling. Care had the
 * most evolved state machine (saving → scheduling → notify → redirect)
 * — those phases are folded in here as the canonical contract.
 *
 * State machine:
 *   idle      — guide hasn't completed yet (component renders nothing)
 *   saving    — running the post-completion mutations (save, archive,
 *               schedule, notify, etc.)
 *   success   — banner + auto-redirect after `redirectAfterMs`
 *   error     — banner + Retry button calling `onRetry`
 *
 * Hosts pass:
 *   • status              — driven by their mutation chain
 *   • onRetry             — invoked by the retry button
 *   • redirectTo          — Wouter href for success auto-navigate
 *   • redirectAfterMs     — default 1500ms; null disables auto-redirect
 *   • successTitle/body   — i18n-ready content
 *
 * Why one component for all heroes: the audit found 5 different
 * completion banners with subtle UX bugs (CheckUp/QultureUp swallowed
 * mutation failures silently). One canonical contract = one place to
 * fix bugs, one analytics surface, one a11y story.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from '@tonnie/locale';
import { AlertCircle, ArrowRight, CheckCircle, Loader2, RefreshCw } from '@tonnie/assets';
import { Button, Card, CardContent, cn } from '@tonnie/ui';

export type FlowCompletionStatus = 'idle' | 'saving' | 'success' | 'error';

export interface FlowCompletionProps {
  status: FlowCompletionStatus;
  /** Optional progress label rendered while saving (e.g. "Saving your results…"). */
  savingLabel?: string;
  successTitle?: string;
  successBody?: string;
  /**
   * Wouter path to navigate to on success. When undefined, the banner
   * stays put and the host owns the redirect.
   */
  redirectTo?: string;
  /**
   * Auto-redirect delay (ms). null disables auto-redirect. Default 1500ms.
   * Long enough for the user to read "complete!"; short enough not to
   * feel sticky.
   */
  redirectAfterMs?: number | null;
  onRetry?: () => void;
  className?: string;
}

export function FlowCompletion({
  status,
  savingLabel,
  successTitle,
  successBody,
  redirectTo,
  redirectAfterMs = 1500,
  onRetry,
  className,
}: FlowCompletionProps) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  // Countdown progress for the auto-redirect (UX Phase D-1).
  // 0 → 1 over `redirectAfterMs`. Drives the visible ring around the
  // Continue button so the user knows a redirect is coming.
  const [countdownProgress, setCountdownProgress] = useState(0);
  // Track whether the user has cancelled the auto-redirect ("Stay here").
  // We don't navigate from this component when cancelled; user can still
  // click Continue manually.
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setCountdownProgress(0);
  }, [status, redirectTo, redirectAfterMs]);

  // Auto-redirect on success — with visible countdown.
  // The pre-2026-04-28 behaviour was a silent setTimeout(setLocation).
  // Users had no idea redirection was imminent and frequently
  // mid-action when the page changed under them. The countdown ring
  // gives a clear "redirecting in 2s" signal AND lets the user opt
  // out via "Stay here" without leaving the surface.
  useEffect(() => {
    if (status !== 'success' || !redirectTo || redirectAfterMs === null) return;
    if (redirectAfterMs <= 0) {
      setLocation(redirectTo);
      return;
    }

    const start = Date.now();
    const tick = () => {
      if (cancelledRef.current) return;
      const elapsed = Date.now() - start;
      const ratio = Math.min(1, elapsed / redirectAfterMs);
      setCountdownProgress(ratio);
      if (ratio >= 1) {
        if (!cancelledRef.current) setLocation(redirectTo);
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    let raf = requestAnimationFrame(tick);
    return () => {
      cancelledRef.current = true;
      cancelAnimationFrame(raf);
    };
  }, [status, redirectTo, redirectAfterMs, setLocation]);

  if (status === 'idle') return null;

  if (status === 'saving') {
    return (
      <Card
        className={className}
        role="status"
        aria-live="polite"
        data-testid="flow-completion-saving"
      >
        <CardContent className="flex items-center gap-3 p-5">
          <Loader2 className="h-5 w-5 animate-spin text-primary-accessible shrink-0" aria-hidden />
          <p className="text-sm">{savingLabel ?? t('copilot.lifecycle.completion.saving')}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === 'error') {
    return (
      <Card
        className={`border-destructive/50 ${className ?? ''}`}
        role="alert"
        aria-live="assertive"
        data-testid="flow-completion-error"
      >
        <CardContent className="space-y-3 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle
              className="h-5 w-5 text-destructive-accessible shrink-0 mt-0.5"
              aria-hidden
            />
            <div className="space-y-1">
              <p className="text-sm font-medium text-destructive-accessible">
                {t('copilot.lifecycle.completion.errorTitle')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('copilot.lifecycle.completion.errorBody')}
              </p>
            </div>
          </div>
          {onRetry && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-2" aria-hidden />
                {t('common.retry')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // success
  const willAutoRedirect =
    !!redirectTo && redirectAfterMs !== null && (redirectAfterMs ?? 0) > 0 && !cancelledRef.current;
  const remainingSeconds = willAutoRedirect
    ? Math.max(0, Math.ceil(((redirectAfterMs ?? 0) * (1 - countdownProgress)) / 1000))
    : 0;

  return (
    <Card
      className={`border-tone-green-line ${className ?? ''}`}
      role="status"
      aria-live="polite"
      data-testid="flow-completion-success"
    >
      <CardContent className="flex items-start gap-3 p-5">
        <CheckCircle className="h-5 w-5 text-tone-green shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-medium">
            {successTitle ?? t('copilot.lifecycle.completion.successTitle')}
          </p>
          <p className="text-sm text-muted-foreground">
            {successBody ?? t('copilot.lifecycle.completion.successBody')}
          </p>
          {willAutoRedirect && (
            <p className="text-xs text-muted-foreground pt-1" aria-live="polite">
              {t('copilot.lifecycle.completion.successRedirect', {
                seconds: remainingSeconds,
              })}
            </p>
          )}
        </div>
        {redirectTo && (
          <div className="flex items-center gap-2 shrink-0">
            <CountdownContinueButton
              progress={willAutoRedirect ? countdownProgress : 1}
              onClick={() => setLocation(redirectTo)}
              label={t('copilot.lifecycle.completion.successContinue')}
            />
            {willAutoRedirect && (
              <button
                type="button"
                onClick={() => {
                  cancelledRef.current = true;
                  // Force a re-render so `willAutoRedirect` recomputes
                  // and the "Redirecting in Xs…" hint disappears.
                  setCountdownProgress(1);
                }}
                className={cn(
                  'text-xs font-medium text-muted-foreground hover:text-foreground transition-colors',
                  'underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm'
                )}
                data-testid="flow-completion-stay"
                aria-label={t('copilot.lifecycle.completion.successStay')}
              >
                {t('copilot.lifecycle.completion.successStay')}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Countdown progress ring around the Continue button. Pure visual —
 * no behavior of its own. Renders an SVG circle whose `stroke-dashoffset`
 * animates from full circumference (0% progress) to 0 (100% progress)
 * over the duration the parent already drives via `progress`.
 *
 * Uses `cubic-bezier(0.5, 0, 0.5, 1)` is unnecessary because the parent
 * animates `progress` smoothly via `requestAnimationFrame` already.
 */
function CountdownContinueButton({
  progress,
  onClick,
  label,
}: {
  progress: number;
  onClick: () => void;
  label: string;
}) {
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.min(1, Math.max(0, progress)));

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative inline-flex items-center gap-1 rounded-full bg-tone-green-solid hover:bg-tone-green-solid/90 text-tone-green-on-solid text-xs font-medium px-3.5 py-1.5',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
      )}
      data-testid="flow-completion-skip"
    >
      <span className="relative z-10 inline-flex items-center gap-1">
        {label}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
      {/* Progress ring overlay */}
      <svg
        className="absolute inset-0 h-full w-full pointer-events-none"
        viewBox="0 0 40 40"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <circle
          cx="20"
          cy="20"
          r={radius}
          stroke="rgba(255, 255, 255, 0.45)"
          strokeWidth="1.5"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 20 20)"
        />
      </svg>
    </button>
  );
}

/**
 * `<FlowErrorBanner>` — drop-in replacement for the various per-host
 * red banners that show up mid-flow when a mutation fails (TelePortal
 * has the most polished version today; the others diverge).
 */
export interface FlowErrorBannerProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export function FlowErrorBanner({
  title,
  message,
  onRetry,
  onDismiss,
  className,
}: FlowErrorBannerProps) {
  const { t } = useTranslation();
  return (
    <Card className={`border-destructive/50 ${className ?? ''}`} role="alert" aria-live="assertive">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle
            className="h-5 w-5 text-destructive-accessible shrink-0 mt-0.5"
            aria-hidden
          />
          <div className="flex-1 space-y-1">
            {title && <p className="text-sm font-medium text-destructive-accessible">{title}</p>}
            <p className="text-sm text-muted-foreground">{message}</p>
          </div>
        </div>
        {(onRetry || onDismiss) && (
          <div className="flex justify-end gap-2">
            {onDismiss && (
              <Button variant="ghost" size="sm" onClick={onDismiss}>
                {t('common.dismiss')}
              </Button>
            )}
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-2" aria-hidden />
                {t('common.retry')}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
